/**
 * Shared suppliers across an insurer's book (task 75, decision 0054). Businesses name their IT provider,
 * cloud services, accounting and payment providers in optional, unscored questions (and their email system
 * in Q021). Names are matched into one supplier list, so the insurer sees how much of its book depends on
 * one supplier, and which insureds are affected when that supplier is breached.
 */
export const SUPPLIER_CATEGORIES = ['it', 'cloud', 'email', 'accounting', 'payments'];
export const SUPPLIER_CATEGORY_LABEL = {
    it: 'IT support',
    cloud: 'Cloud services',
    email: 'Email',
    accounting: 'Accounting and payroll',
    payments: 'Payments',
};
/** The question each category is read from. Email reuses Q021 rather than asking again. */
export const SUPPLIER_QUESTIONS = {
    it: 'SUP-IT',
    cloud: 'SUP-CLOUD',
    email: 'Q021',
    accounting: 'SUP-ACCT',
    payments: 'SUP-PAY',
};
/** Suppliers known by more than one name; anything else is matched on its cleaned-up name. */
export const KNOWN_SUPPLIERS = [
    { key: 'microsoft-365', name: 'Microsoft 365', aliases: ['microsoft 365', 'office 365', 'o365', 'm365', 'ms 365', 'ms365', 'microsoft office 365', 'exchange online', 'outlook', 'onedrive', 'sharepoint', 'microsoft teams', 'teams'] }, // prettier-ignore
    { key: 'google-workspace', name: 'Google Workspace', aliases: ['google workspace', 'g suite', 'gsuite', 'gmail', 'google drive', 'google apps'] }, // prettier-ignore
    { key: 'aws', name: 'Amazon Web Services', aliases: ['aws', 'amazon web services', 'amazon aws', 'amazon s3', 's3'] }, // prettier-ignore
    { key: 'azure', name: 'Microsoft Azure', aliases: ['azure', 'microsoft azure'] },
    { key: 'google-cloud', name: 'Google Cloud', aliases: ['google cloud', 'gcp', 'google cloud platform'] },
    { key: 'dropbox', name: 'Dropbox', aliases: ['dropbox'] },
    { key: 'xero', name: 'Xero', aliases: ['xero'] },
    { key: 'quickbooks', name: 'QuickBooks', aliases: ['quickbooks', 'quickbooks online', 'qbo', 'intuit quickbooks'] }, // prettier-ignore
    { key: 'sql-account', name: 'SQL Account', aliases: ['sql account', 'sql accounting', 'sql estream', 'estream sql'] }, // prettier-ignore
    { key: 'autocount', name: 'AutoCount', aliases: ['autocount', 'auto count', 'autocount accounting'] },
    { key: 'ipay88', name: 'iPay88', aliases: ['ipay88', 'ipay 88'] },
    { key: 'stripe', name: 'Stripe', aliases: ['stripe'] },
    { key: 'billplz', name: 'Billplz', aliases: ['billplz'] },
    { key: 'razer-merchant-services', name: 'Razer Merchant Services', aliases: ['razer merchant services', 'razer pay', 'molpay', 'razer'] }, // prettier-ignore
    { key: 'paypal', name: 'PayPal', aliases: ['paypal'] },
    { key: 'senangpay', name: 'senangPay', aliases: ['senangpay', 'senang pay'] },
];
const LEGAL = /\b(sdn\.? ?bhd|sendirian berhad|berhad|bhd|pte\.? ?ltd|pty\.? ?ltd|ltd|limited|llc|inc|plc|corp(oration)?|co)\.?$/; // prettier-ignore
/** Answers that name no supplier. */
const NONE = new Set(['none', 'no', 'nil', 'na', 'n a', 'not applicable', 'nobody', 'no one', 'ourselves', 'we do it ourselves', 'in house', 'inhouse', 'internal', 'not sure', 'dont know', 'unknown']); // prettier-ignore
/** A name cleaned for matching: lower-case, plain letters, no punctuation or company suffix. */
export function cleanSupplierName(name) {
    let s = name
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[’']/g, '')
        .replace(/[^a-z0-9.]+/g, ' ')
        .trim();
    for (let i = 0; i < 2; i++)
        s = s
            .replace(LEGAL, '')
            .replace(/[.\s]+$/, '')
            .trim();
    return s.replace(/\./g, ' ').replace(/\s+/g, ' ').replace(/^the /, '').trim();
}
const ALIAS = new Map(KNOWN_SUPPLIERS.flatMap((k) => k.aliases.map((a) => [cleanSupplierName(a), k]))); // prettier-ignore
/** One supplier's key and display name, from any way of writing it; null for "none" and the like. */
export function supplierOf(name) {
    const clean = cleanSupplierName(name);
    if (!clean || NONE.has(clean) || clean.length < 2)
        return null;
    const known = ALIAS.get(clean);
    if (known)
        return { key: known.key, name: known.name };
    return { key: clean.replace(/ /g, '-').slice(0, 80), name: name.trim().replace(/\s+/g, ' ').slice(0, 120) };
}
/** Q021's options, as suppliers ("Other / hosted" names none). */
const EMAIL_OPTION = { microsoft_365: 'Microsoft 365', google_workspace: 'Google Workspace' }; // prettier-ignore
/** The suppliers a business named, by category, from its answers. */
export function suppliersOf(answers) {
    // prettier-ignore
    const out = [];
    for (const category of SUPPLIER_CATEGORIES) {
        const v = answers[SUPPLIER_QUESTIONS[category]];
        const names = category === 'email' ? (typeof v === 'string' && EMAIL_OPTION[v] ? [EMAIL_OPTION[v]] : []) : Array.isArray(v) ? v : typeof v === 'string' ? [v] : []; // prettier-ignore
        for (const n of names) {
            if (typeof n !== 'string')
                continue;
            // "Xero, AutoCount" in one box is two suppliers ("Lee and Partners" is one).
            for (const part of n.split(/[,;\n]/)) {
                const s = supplierOf(part);
                if (s && !out.some((o) => o.category === category && o.key === s.key))
                    out.push({ category, ...s });
            }
        }
    }
    return out;
}
/** The supplier list across a book: who depends on whom, most-shared first. */
export function supplierBook(clients) {
    const rows = new Map();
    let answered = 0;
    for (const c of clients) {
        const named = suppliersOf(c.answers);
        if (named.length)
            answered++;
        for (const key of new Set(named.map((n) => n.key))) {
            const mine = named.filter((n) => n.key === key);
            const r = rows.get(key) ?? { key, name: '', categories: [], clients: 0, share: 0, sum_insured: 0, premium: 0, names: new Map() }; // prettier-ignore
            r.clients++;
            r.sum_insured += c.sum_insured ?? 0;
            r.premium += c.premium ?? 0;
            for (const n of mine) {
                if (!r.categories.includes(n.category))
                    r.categories.push(n.category);
                r.names.set(n.name, (r.names.get(n.name) ?? 0) + 1);
            }
            rows.set(key, r);
        }
    }
    const suppliers = [...rows.values()].map(({ names, ...r }) => ({
        ...r,
        // The way most clients wrote it.
        name: [...names].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0][0],
        categories: SUPPLIER_CATEGORIES.filter((k) => r.categories.includes(k)),
        share: answered ? r.clients / answered : 0,
    }));
    suppliers.sort((a, b) => b.clients - a.clients || b.sum_insured - a.sum_insured || a.name.localeCompare(b.name)); // prettier-ignore
    return { answered, total: clients.length, suppliers };
}
/** The insureds affected if one supplier is breached, largest sum insured first. */
export function supplierDetail(clients, key) {
    const row = supplierBook(clients).suppliers.find((s) => s.key === key);
    if (!row)
        return null;
    const affected = clients.flatMap(({ answers, ...c }) => {
        const mine = suppliersOf(answers).filter((n) => n.key === key);
        return mine.length ? [{ ...c, categories: SUPPLIER_CATEGORIES.filter((k) => mine.some((m) => m.category === k)) }] : []; // prettier-ignore
    });
    affected.sort((a, b) => (b.sum_insured ?? 0) - (a.sum_insured ?? 0) || a.customer_name.localeCompare(b.customer_name)); // prettier-ignore
    return { key, name: row.name, categories: row.categories, clients: affected, sum_insured: row.sum_insured, premium: row.premium }; // prettier-ignore
}
//# sourceMappingURL=suppliers.js.map